'use client';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { TrackedAnchor } from '@/components/ancher';
import { TrackedButton } from '@/components/button';
import {
  BLOG_CATEGORIES,
  BLOG_CATEGORY_KEYS,
  TRACKED_EVENT_POSITION,
  getBlogCategoryPath,
  type BlogCategory,
} from '@/constants';
import type { PostsByCategory } from '../load-list-data';
import BlogPostCard from '@/components/blog-post-card';

const CATEGORY_PARAM = 'category';

type IndicatorRect = { left: number; width: number };

/**
 * 패널 id 는 카테고리별이 아니라 **고정값**이다 — 활성 탭의 패널 하나만 렌더하므로
 * 카테고리를 붙이면 비활성 탭 6개의 `aria-controls` 가 존재하지 않는 id 를 가리킨다.
 */
const PANEL_ID = 'blog-category-panel';

/**
 * 활성 탭은 `?category=` 로 URL 에 남긴다 — 글을 열었다 뒤로 왔을 때 보고 있던 탭이
 * 유지되게 하려는 것이다. `replaceState` 로 **현재 히스토리 항목을 갱신**하므로 탭을 여러 번
 * 눌러도 히스토리가 쌓이지 않고, 뒤로가기 한 번에 이 페이지를 벗어난다.
 *
 * ⚠️ 서버에서 `searchParams` 를 읽지 않는다 — 읽으면 이 페이지가 프리렌더(SSG)를 잃고 요청마다
 * SSR 로 떨어진다. 대신 마운트 후 `location.search` 를 읽어 복원하므로, 파라미터를 달고 들어온
 * 첫 프레임만 '전체' 탭이었다가 즉시 해당 탭으로 바뀐다.
 *
 * 탭은 md 부터 `flex-1` 등분이다 — 자연 폭으로 두면 7개 합(≈708)이 줄 너비보다 좁아
 * 오른쪽이 빈다. 다만 `min-w-fit` 을 함께 걸어 자기 텍스트보다 좁아지지는 않게 한다
 * (영문 라벨이 길어 좁은 폭에서 등분하면 잘린다). tablist 의 `min-w-max` 는 그때
 * 줄을 컨테이너보다 넓게 유지해 가로 스크롤로 넘긴다.
 *
 * ⚠️ 인디케이터는 **활성 탭을 실측해서** 옮긴다 — 모바일 탭은 글자 길이만큼의 폭이라
 * CSS 로 위치를 계산할 수 없다.
 */
export default function CategoryTabsSection({
  postsByCategory,
}: {
  postsByCategory: PostsByCategory;
}) {
  const t = useTranslations('Blog.main');
  const tLanding = useTranslations('Blog.landing');
  const [activeIndex, setActiveIndex] = useState(0);
  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
  const tabListRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // 뒤로가기로 돌아왔을 때 보고 있던 탭을 복원한다. 모르는 값이면 무시하고 '전체' 로 둔다
  useEffect(() => {
    const param = new URLSearchParams(window.location.search).get(
      CATEGORY_PARAM,
    );
    const restored = BLOG_CATEGORIES.indexOf(param as BlogCategory);

    if (restored > 0) setActiveIndex(restored);
  }, []);

  /**
   * Next 라우터가 아니라 History API 를 직접 쓴다 — 라우터를 쓰면 RSC 왕복이 생기고
   * 스크롤이 위로 튄다. 기본 탭('전체')에서는 파라미터를 빼 `/blog` 를 깨끗하게 유지한다.
   */
  const selectTab = (next: number) => {
    setActiveIndex(next);

    const url = new URL(window.location.href);
    if (next === 0) url.searchParams.delete(CATEGORY_PARAM);
    else url.searchParams.set(CATEGORY_PARAM, BLOG_CATEGORIES[next]);

    window.history.replaceState(null, '', url);
  };

  const activeCategory = BLOG_CATEGORIES[activeIndex];
  const posts = postsByCategory[activeCategory] ?? [];

  const measure = useCallback(() => {
    const tab = tabRefs.current[activeIndex];
    if (!tab) return;

    // offsetLeft 는 스크롤과 무관한 컨테이너 기준 좌표라, 가로 스크롤된 상태에서도
    // 인디케이터가 탭과 함께 움직인다(같은 스크롤 컨테이너 안에 있으므로)
    setIndicator({ left: tab.offsetLeft, width: tab.offsetWidth });
  }, [activeIndex]);

  useEffect(() => {
    measure();

    // 폰트 로드·리사이즈·로케일별 글자 길이 변화로 탭 폭이 바뀌면 다시 잰다
    const list = tabListRef.current;
    if (!list || typeof ResizeObserver === 'undefined') return;

    const observer = new ResizeObserver(measure);
    observer.observe(list);
    return () => observer.disconnect();
  }, [measure]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;

    event.preventDefault();
    const delta = event.key === 'ArrowLeft' ? -1 : 1;
    const next =
      (activeIndex + delta + BLOG_CATEGORIES.length) % BLOG_CATEGORIES.length;
    selectTab(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section className="flex flex-col gap-5 lg:gap-[60px]">
      <div className="flex flex-col gap-2.5">
        <h2 className="text-h5 text-3b-text-primary lg:text-h4">
          {tLanding('categoryTitle')}
        </h2>
        <p className="text-sh1 font-semibold text-[#6c7380] lg:text-h6">
          {tLanding('categoryDescription')}
        </p>
      </div>

      <div className="flex flex-col gap-5 lg:gap-10">
        {/* 탭 줄과 카드 줄은 각자 스크롤한다 — 1260 콘텐츠가 안 들어가는 폭에서 따로 밀린다.
            overflow-x-auto 만 두면 overflow-y 도 auto 로 계산돼(CSS 사양) 1px 만 넘쳐도 탭 줄에
            세로 스크롤바가 뜬다(HOME-750). 탭 줄은 세로로 스크롤할 이유가 없으니 hidden 으로 막는다 */}
        <div className="-mx-5 overflow-x-auto overflow-y-hidden px-5 md:-mx-8 md:px-8">
          <div
            ref={tabListRef}
            role="tablist"
            aria-label={tLanding('categoryTitle')}
            onKeyDown={onKeyDown}
            className="relative flex min-w-max border-b-[3px] border-solid border-3b-background-200"
          >
            {BLOG_CATEGORIES.map((category, i) => (
              <TrackedButton
                key={category}
                ref={node => {
                  tabRefs.current[i] = node;
                }}
                label={`blog_category_tab_${category}`}
                position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
                role="tab"
                type="button"
                id={`blog-category-tab-${category}`}
                aria-selected={i === activeIndex}
                aria-controls={PANEL_ID}
                tabIndex={i === activeIndex ? 0 : -1}
                onClick={() => selectTab(i)}
                className={`whitespace-nowrap px-[26px] pb-5 text-body1 font-semibold transition-colors md:min-w-fit md:flex-1 md:px-0 lg:text-h6 ${
                  i === activeIndex ? 'text-[#288bfd]' : 'text-3b-text-primary'
                }`}
              >
                {t(`categories.${BLOG_CATEGORY_KEYS[i]}.label`)}
              </TrackedButton>
            ))}
            <span
              aria-hidden="true"
              className="absolute -bottom-[3px] h-[3px] bg-[#288bfd] transition-[left,width] duration-300 ease-out motion-reduce:transition-none"
              style={{
                left: indicator?.left ?? 0,
                width: indicator?.width ?? 0,
              }}
            />
          </div>
        </div>

        <div
          role="tabpanel"
          id={PANEL_ID}
          aria-labelledby={`blog-category-tab-${activeCategory}`}
          className="-mx-5 overflow-x-auto px-5 md:-mx-8 md:px-8"
        >
          <ul className="flex min-w-max gap-5 lg:min-w-0">
            {posts.map(post => (
              <li key={post.id} className="lg:flex-1">
                {/* label 은 인덱스가 아니라 글 id 다 — 탭을 바꾸면 같은 자리에 다른 글이
                    오므로 인덱스로는 GA 에서 어떤 글을 눌렀는지 구분되지 않는다
                    (구형 목록 카드의 `blog_item-{id}` 와 같은 계열) */}
                <BlogPostCard
                  post={post}
                  label={`blog_category_post_${post.id}`}
                  variant="carousel"
                />
              </li>
            ))}
          </ul>
        </div>

        {/* 더 보기 버튼을 이 래퍼 안에 둔다 — 본문과의 간격이 40(모바일 20)이어야 하고,
            제목 블록과 본문 사이 간격(60)과는 다르다 */}
        <TrackedAnchor
          href={getBlogCategoryPath(activeCategory)}
          label={`blog_category_more_${activeCategory}`}
          position={TRACKED_EVENT_POSITION.MAIN_CONTENT}
          prefetch
          className="focus-ring mx-auto rounded-[10px] bg-3b-background-200 px-3.5 py-2.5 text-body2 font-semibold text-3b-text-primary transition-colors hover:bg-3b-blue-light-900 hover:text-3b-text-contrast lg:px-6 lg:text-sh2"
        >
          {tLanding('categoryMore')}
        </TrackedAnchor>
      </div>
    </section>
  );
}
